import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useRatingDistribution } from '@/hooks/dashboard/useRatingDistribution'
import { bookListUrl } from '@/lib/utils'
import { ChartContainer } from '../ChartContainer'

// Types for Recharts custom components
interface TooltipPayload {
  value: number
  payload: {
    rating: number
    book_count: number
    percentage: number
  }
}

interface CustomTooltipProps {
  active?: boolean
  payload?: TooltipPayload[]
  label?: string
}

export function RatingDistributionChart() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, averageRating, totalRatedBooks, isLoading, error } = useRatingDistribution()

  const title = t('dashboard.charts.ratingDistribution')
  const description =
    averageRating > 0
      ? `${t('dashboard.charts.averageRating')}: ${averageRating.toFixed(1)} ⭐ (${totalRatedBooks} ${t('dashboard.charts.ratedBooks')})`
      : t('dashboard.charts.noRatedBooks')

  // Create star labels for x-axis - ensure all ratings 1-5 are present
  const chartData = [1, 2, 3, 4, 5].map((rating) => {
    const ratingData = data.find((item) => item.rating === rating)
    return {
      rating,
      book_count: ratingData?.book_count || 0,
      percentage: ratingData?.percentage || 0,
      stars: '⭐'.repeat(rating),
      starLabel: `${rating}⭐`,
      label: `${rating} ${rating === 1 ? t('dashboard.charts.star') : t('dashboard.charts.stars')}`,
    }
  })

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-foreground mb-2">{'⭐'.repeat(data.rating)}</p>
          <div className="space-y-1 text-sm">
            <p className="text-muted-foreground">
              {t('dashboard.charts.books')}:{' '}
              <span className="font-medium text-foreground">{data.book_count}</span>
            </p>
            <p className="text-muted-foreground">
              {t('dashboard.charts.percentage')}:{' '}
              <span className="font-medium text-foreground">{data.percentage}%</span>
            </p>
          </div>
        </div>
      )
    }
    return null
  }

  // Show empty state if no data
  if (!isLoading && !error && (data.length === 0 || totalRatedBooks === 0)) {
    return (
      <ChartContainer title={title} description={t('dashboard.charts.noRatedBooks')}>
        <div className="flex items-center justify-center h-full text-center text-muted-foreground">
          <div>
            <div className="text-4xl mb-4">⭐</div>
            <p className="font-medium mb-1">{t('dashboard.charts.noRatingsTitle')}</p>
            <p className="text-sm">{t('dashboard.charts.rateYourBooks')}</p>
          </div>
        </div>
      </ChartContainer>
    )
  }

  // Define heterogeneous colors for ratings (using palette from roulette system)
  const getBarColor = (rating: number) => {
    switch (rating) {
      case 5:
        return '#10B981' // Emerald - Excellent
      case 4:
        return '#06B6D4' // Cyan - Very Good
      case 3:
        return '#F59E0B' // Amber - Good
      case 2:
        return '#EC4899' // Pink - Fair
      case 1:
        return '#EF4444' // Red - Poor
      default:
        return '#9CA3AF' // Gray fallback
    }
  }

  return (
    <ChartContainer
      title={title}
      description={description}
      isLoading={isLoading}
      error={error?.message || null}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 25,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="starLabel"
            axisLine={false}
            tickLine={false}
            className="text-xs text-muted-foreground"
            interval={0}
            tick={{ fontSize: 12 }}
          />
          <YAxis axisLine={false} tickLine={false} className="text-xs text-muted-foreground" />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="book_count" radius={[4, 4, 0, 0]}>
            {chartData.map((entry) => (
              <Cell
                key={entry.rating}
                fill={getBarColor(entry.rating)}
                cursor="pointer"
                onClick={() => navigate(bookListUrl({ status: 'all', rating: entry.rating }))}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
